// Enhanced Heartbeat Audio System - Safari Compatible Version
class GlobalHeartbeatAudio {
  constructor() {
    this.audioContext = null;
    this.oscillators = [];
    this.enabled = true;
    this.isMobile = false;
    this.currentPhase = 0;
    this.heartbeatActive = false;
    this.isInitialized = false;
    this.isSafari = false;
    
    this.init();
  }

  // Grant Math Formula
  getGrantMathValue(theta, alpha = 1.618) {
    const adjustedTheta = (theta * Math.PI / 180) % (2 * Math.PI);
    const denominator = 2 * (-adjustedTheta + 3 * Math.PI);
    
    if (Math.abs(denominator) < 0.01) {
      return 0.8;
    }
    
    const R = (alpha * Math.PI) / denominator;
    const normalizedR = Math.abs(R) % 2;
    return Math.max(0.3, Math.min(1.2, normalizedR));
  }

  getDualGrantSpirals(phase) {
    const spiral1 = this.getGrantMathValue(phase, 1.618);
    const spiral2 = this.getGrantMathValue(phase + 180, 1.618);
    const intersection = (spiral1 + spiral2) / 2;
    return { spiral1, spiral2, intersection };
  }

  async init() {
    if (this.isInitialized) return;
    
    // Detect Safari
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // Detect mobile
    this.isMobile = window.innerWidth < 600 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    console.log('🫀 AUDIO INIT: Safari:', this.isSafari, 'Mobile:', this.isMobile);

    // Initialize sonic layer tracking
    this.sonicLayers = [];

    try {
      // Create AudioContext with Safari-specific handling
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      console.log('🎵 AUDIO: Context created, state:', this.audioContext.state);
      
      // Safari-specific initialization
      if (this.isSafari) {
        this.setupSafariAudio();
      } else {
        this.setupStandardAudio();
      }
      
      console.log('🫀 HEARTBEAT: Enhanced audio system initialized (Safari compatible)');
    } catch (error) {
      console.log('🫀 HEARTBEAT: Audio not available -', error.message);
      this.enabled = false;
    }
  }

  setupSafariAudio() {
    // Safari requires more explicit user interaction
    const safariInteractionEvents = ['click', 'touchstart', 'keydown'];
    
    const startSafariAudio = async () => {
      if (this.isInitialized) return;
      
      try {
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
          console.log('🍎 SAFARI: Audio context resumed successfully');
        }
        
        if (this.enabled && !this.heartbeatActive) {
          this.isInitialized = true;
          this.startHeartbeat();
          console.log('🍎 SAFARI: Heartbeat started successfully');
        }
      } catch (error) {
        console.log('🍎 SAFARI: Audio start failed:', error);
      }
      
      // Remove listeners after successful start
      safariInteractionEvents.forEach(event => {
        document.removeEventListener(event, startSafariAudio);
      });
    };

    // Add Safari-specific listeners
    safariInteractionEvents.forEach(event => {
      document.addEventListener(event, startSafariAudio, { once: true });
    });

    // Also try immediate start for Safari
    setTimeout(() => {
      if (!this.isInitialized && this.audioContext?.state === 'running') {
        startSafariAudio();
      }
    }, 1000);
  }

  setupStandardAudio() {
    // Standard browser setup
    const interactionEvents = ['click', 'touchstart', 'keydown', 'mousemove'];
    
    const startAudio = async () => {
      if (this.isInitialized) return;
      
      try {
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
          console.log('🎵 AUDIO: Context resumed successfully');
        }
        
        if (this.enabled && !this.heartbeatActive) {
          this.isInitialized = true;
          this.startHeartbeat();
          console.log('🎵 AUDIO: Heartbeat auto-started successfully');
        }
      } catch (error) {
        console.log('🎵 AUDIO: Auto-start failed:', error);
      }
      
      interactionEvents.forEach(event => {
        document.removeEventListener(event, startAudio);
      });
    };

    interactionEvents.forEach(event => {
      document.addEventListener(event, startAudio, { once: true });
    });

    // Try immediate start
    setTimeout(() => {
      if (!this.isInitialized && this.audioContext?.state === 'running') {
        startAudio();
      }
    }, 500);
  }

  startHeartbeat() {
    if (!this.enabled || !this.audioContext || this.heartbeatActive) return;

    this.heartbeatActive = true;
    console.log('🫀 HEARTBEAT: Starting heartbeat cycle...');

    try {
      // Sacred heartbeat frequencies - Enhanced for Safari
      const heartbeatFreqs = [55, 82.5, 110, 165];
      
      heartbeatFreqs.forEach((baseFreq, index) => {
        setTimeout(() => {
          const spirals = this.getDualGrantSpirals(this.currentPhase + index * 90);
          const modulatedFreq = baseFreq * spirals.intersection;
          this.playHeartbeatPulse(modulatedFreq, 2000 + index * 400);
          console.log(`🎵 PLAYING: ${modulatedFreq.toFixed(1)}Hz pulse ${index + 1}/4`);
        }, index * 300);
      });

      // Evolve the phase
      this.currentPhase = (this.currentPhase + 30) % 360;

      // Schedule next cycle (Safari-friendly timing)
      setTimeout(() => {
        if (this.enabled && this.audioContext && this.audioContext.state === 'running') {
          this.heartbeatActive = false;
          this.startHeartbeat();
        }
      }, this.isSafari ? 8000 : 6000); // Longer intervals for Safari

    } catch (error) {
      console.log('🫀 HEARTBEAT: Generation failed -', error.message);
      this.heartbeatActive = false;
    }
  }

  playHeartbeatPulse(frequency, duration = 2000) {
    if (!this.enabled || !this.audioContext || this.audioContext.state !== 'running') {
      console.log('🔇 AUDIO: Cannot play - context not running');
      return;
    }

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      const filterNode = this.audioContext.createBiquadFilter();

      oscillator.connect(filterNode);
      filterNode.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      oscillator.type = 'sine';
      
      // Safari-friendly filtering
      filterNode.type = 'lowpass';
      filterNode.frequency.setValueAtTime(this.isSafari ? 600 : 800, this.audioContext.currentTime);
      filterNode.Q.setValueAtTime(1.2, this.audioContext.currentTime);

      // Enhanced volume for Safari
      const now = this.audioContext.currentTime;
      const durationSeconds = duration / 1000;
      const safariVolumeBoost = this.isSafari ? 1.5 : 1.0;
      
      gainNode.gain.setValueAtTime(0, now);
      
      // First pulse (stronger) - Safari enhanced
      gainNode.gain.linearRampToValueAtTime(0.2 * safariVolumeBoost, now + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.03 * safariVolumeBoost, now + 0.4);
      
      // Brief pause
      gainNode.gain.linearRampToValueAtTime(0.01 * safariVolumeBoost, now + 0.6);
      
      // Second pulse (weaker)
      gainNode.gain.linearRampToValueAtTime(0.15 * safariVolumeBoost, now + 0.8);
      gainNode.gain.exponentialRampToValueAtTime(0.01 * safariVolumeBoost, now + durationSeconds);

      oscillator.start(now);
      oscillator.stop(now + durationSeconds);
      
      this.oscillators.push(oscillator);
      
      console.log(`🫀 ${this.isSafari ? 'SAFARI' : 'STANDARD'} HEARTBEAT: ${frequency.toFixed(1)}Hz`);

      oscillator.onended = () => {
        const index = this.oscillators.indexOf(oscillator);
        if (index > -1) {
          this.oscillators.splice(index, 1);
        }
      };

    } catch (error) {
      console.log('🫀 HEARTBEAT: Pulse creation failed -', error.message);
    }
  }

  // Enhanced control methods
  setEnabled(enabled) {
    this.enabled = enabled;
    console.log(`🫀 CONTROL: Heartbeat ${enabled ? 'enabled' : 'disabled'}`);
    
    if (!enabled) {
      this.stopAll();
    } else if (this.audioContext && this.audioContext.state === 'running' && !this.heartbeatActive) {
      setTimeout(() => this.startHeartbeat(), 1000);
    }
    
    return this.enabled;
  }

  forceRestart() {
    console.log('🔄 RESTART: Force restarting heartbeat...');
    this.heartbeatActive = false;
    this.stopAll();
    
    if (this.enabled && this.audioContext) {
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume().then(() => {
          setTimeout(() => this.startHeartbeat(), 500);
        });
      } else {
        setTimeout(() => this.startHeartbeat(), 500);
      }
    }
  }

  stopAll() {
    this.oscillators.forEach(osc => {
      try {
        if (osc.stop) osc.stop();
        if (osc.disconnect) osc.disconnect();
      } catch (e) {
        // Oscillator may already be stopped
      }
    });
    this.oscillators = [];
    this.heartbeatActive = false;
  }

  testHeartbeat() {
    console.log('🧪 TESTING: Manual heartbeat test triggered');
    
    if (!this.audioContext) {
      console.log('❌ TEST: No audio context');
      return false;
    }
    
    if (this.audioContext.state === 'suspended') {
      console.log('🔄 TEST: Resuming suspended context...');
      this.audioContext.resume().then(() => {
        this.playTestTone();
      });
    } else {
      this.playTestTone();
    }
    
    return true;
  }

  playTestTone() {
    console.log('🎵 TEST: Playing test tone...');
    
    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      oscillator.frequency.setValueAtTime(440, this.audioContext.currentTime);
      oscillator.type = 'sine';
      
      const testVolume = this.isSafari ? 0.3 : 0.2; // Higher volume for Safari
      
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(testVolume, this.audioContext.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 1);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 1);
      
      console.log(`✅ TEST: ${this.isSafari ? 'Safari' : 'Standard'} test tone should be audible now!`);
    } catch (error) {
      console.log('❌ TEST: Test tone failed:', error);
    }
  }

  get status() {
    return {
      enabled: this.enabled,
      isMobile: this.isMobile,
      isSafari: this.isSafari,
      isInitialized: this.isInitialized,
      heartbeatActive: this.heartbeatActive,
      contextState: this.audioContext?.state || 'none',
      oscillatorCount: this.oscillators.length,
      currentPhase: this.currentPhase
    };
  }

  getDebugInfo() {
    console.log('🔍 HEARTBEAT DEBUG INFO:');
    console.log('  - Enabled:', this.enabled);
    console.log('  - Safari:', this.isSafari);
    console.log('  - Mobile:', this.isMobile);
    console.log('  - Initialized:', this.isInitialized);
    console.log('  - Active:', this.heartbeatActive);
    console.log('  - Context state:', this.audioContext?.state);
    console.log('  - Oscillators:', this.oscillators.length);
    console.log('  - Phase:', this.currentPhase);
    return this.status;
  }

  // Sonic layer integration methods
  addSonicLayer(sonicEngine) {
    if (!this.sonicLayers) {
      this.sonicLayers = [];
    }
    this.sonicLayers.push(sonicEngine);
    console.log('🎵 HEARTBEAT: Added sonic layer for integration');
  }

  removeSonicLayer() {
    if (this.sonicLayers) {
      this.sonicLayers = [];
    }
    console.log('🎵 HEARTBEAT: Removed sonic layers');
  }
}

// Global instance
window.planetaryFedHeartbeat = window.planetaryFedHeartbeat || new GlobalHeartbeatAudio();

// Enhanced Cosmic Audio Engine with Tibetan Bowl Harmonics
class CosmicAudioEngine {
  constructor() {
    this.audioContext = null;
    this.masterGain = null;
    this.oscillators = [];
    this.effects = {};
    this.isPlaying = false;
    this.dimensions = {
      r: 1.618, theta: 0, phi: 0, t: 1.0, f: 528, u: 1.0, infinity: 3.14159
    };
  }

  async init() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.masterGain = this.audioContext.createGain();
      this.masterGain.gain.value = 0.12; // Gentle volume for persistence
      this.masterGain.connect(this.audioContext.destination);
      this.setupTibetanBowlEffects();
      return true;
    } catch (error) {
      console.error('Cosmic audio initialization failed:', error);
      return false;
    }
  }

  setupTibetanBowlEffects() {
    // Sacred Reverb for Temple Space
    this.effects.reverb = this.audioContext.createConvolver();
    this.generateSacredReverb();
    this.effects.reverb.connect(this.masterGain);
    
    // Warm Filter for Organic Tibetan Sound
    this.effects.filter = this.audioContext.createBiquadFilter();
    this.effects.filter.type = 'lowpass';
    this.effects.filter.frequency.value = 2500;
    this.effects.filter.Q.value = 1.0;
    this.effects.filter.connect(this.effects.reverb);
    
    // Sacred Echo for Temple Acoustics
    this.effects.delay = this.audioContext.createDelay(2.0);
    this.effects.delay.delayTime.value = 0.6;
    this.effects.delayFeedback = this.audioContext.createGain();
    this.effects.delayFeedback.gain.value = 0.2;
    
    this.effects.delay.connect(this.effects.delayFeedback);
    this.effects.delayFeedback.connect(this.effects.delay);
    this.effects.delay.connect(this.effects.filter);
  }

  generateSacredReverb() {
    const length = this.audioContext.sampleRate * 3;
    const impulse = this.audioContext.createBuffer(2, length, this.audioContext.sampleRate);
    
    for (let channel = 0; channel < 2; channel++) {
      const channelData = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        const decay = Math.pow(1 - i / length, 1.5);
        channelData[i] = (Math.random() * 2 - 1) * decay * 0.3;
      }
    }
    
    this.effects.reverb.buffer = impulse;
  }

  async startCosmicSounds() {
    if (!this.audioContext) {
      const initialized = await this.init();
      if (!initialized) return false;
    }

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    this.isPlaying = true;
    this.createTibetanBowlOscillators();
    return true;
  }

  createTibetanBowlOscillators() {
    this.stopCosmicSounds();
    this.isPlaying = true;
    this.oscillators = [];

    const baseFreq = this.dimensions.f;
    
    // ENHANCED: Tibetan Bowl + Horn Layers
    this.createBowlLayer('fundamental', baseFreq, 0.25); // Love frequency foundation
    this.createBowlLayer('harmonic_2nd', baseFreq * 2.1, 0.15);
    this.createBowlLayer('golden_bowl', baseFreq * this.dimensions.r, 0.18);
    this.createBowlLayer('deep_meditation', baseFreq / 2, 0.20);
    
    // NEW: Tibetan Low Horns (Deep, resonant)
    this.createTibetanHorn('deep_horn_1', 80, 0.22); // Deep foundation
    this.createTibetanHorn('deep_horn_2', 120, 0.18); // Harmonic support
    this.createTibetanHorn('ceremonial_horn', 160, 0.14); // Ceremonial overtone
    
    // Love Frequency Default (528Hz with gentle harmonics)
    this.createLoveFrequencyLayer('love_fundamental', 528, 0.28);
    this.createLoveFrequencyLayer('love_harmonic', 528 * 1.618, 0.16);
  }

  createTibetanHorn(name, frequency, volume) {
    if (!this.isPlaying || !this.audioContext) return;

    // Create rich harmonic horn sound
    const hornGroup = [];
    
    // Fundamental frequency (darker, richer)
    const fundamental = this.audioContext.createOscillator();
    const fundamentalGain = this.audioContext.createGain();
    const fundamentalFilter = this.audioContext.createBiquadFilter();
    
    fundamental.type = 'sawtooth'; // Rich harmonics like horn
    fundamental.frequency.value = frequency;
    
    // Horn-like filtering (emphasize low frequencies)
    fundamentalFilter.type = 'lowpass';
    fundamentalFilter.frequency.value = frequency * 3;
    fundamentalFilter.Q.value = 2.0;
    
    // Very slow attack like blowing horn
    fundamentalGain.gain.setValueAtTime(0, this.audioContext.currentTime);
    fundamentalGain.gain.exponentialRampToValueAtTime(volume, this.audioContext.currentTime + 2);
    
    // Slow tremolo like human breath
    const tremolo = this.audioContext.createOscillator();
    const tremoloGain = this.audioContext.createGain();
    tremolo.type = 'sine';
    tremolo.frequency.value = 0.8; // Breathing rate
    tremoloGain.gain.value = volume * 0.3;
    
    tremolo.connect(tremoloGain);
    tremoloGain.connect(fundamentalGain.gain);
    
    fundamental.connect(fundamentalFilter);
    fundamentalFilter.connect(fundamentalGain);
    fundamentalGain.connect(this.effects.delay);

    fundamental.start();
    tremolo.start();

    this.oscillators.push({ 
      name, 
      oscillators: [fundamental], 
      gainNodes: [fundamentalGain],
      lfoNodes: [tremolo],
      type: 'tibetan_horn'
    });
  }

  createLoveFrequencyLayer(name, frequency, volume) {
    if (!this.isPlaying || !this.audioContext) return;

    const loveOsc = this.audioContext.createOscillator();
    const loveGain = this.audioContext.createGain();
    const loveFilter = this.audioContext.createBiquadFilter();
    
    // Pure sine for love frequency
    loveOsc.type = 'sine';
    loveOsc.frequency.value = frequency;
    
    // Gentle filtering for warmth
    loveFilter.type = 'lowpass';
    loveFilter.frequency.value = frequency * 2;
    loveFilter.Q.value = 0.8;
    
    // Very gentle attack
    loveGain.gain.setValueAtTime(0, this.audioContext.currentTime);
    loveGain.gain.exponentialRampToValueAtTime(volume, this.audioContext.currentTime + 3);
    
    // Love frequency shimmer (very subtle)
    const loveShimmer = this.audioContext.createOscillator();
    const shimmerGain = this.audioContext.createGain();
    loveShimmer.type = 'sine';
    loveShimmer.frequency.value = 0.618; // Golden ratio LFO
    shimmerGain.gain.value = frequency * 0.005;
    
    loveShimmer.connect(shimmerGain);
    shimmerGain.connect(loveOsc.frequency);
    
    loveOsc.connect(loveFilter);
    loveFilter.connect(loveGain);
    loveGain.connect(this.effects.filter);

    loveOsc.start();
    loveShimmer.start();

    this.oscillators.push({ 
      name, 
      oscillators: [loveOsc], 
      gainNodes: [loveGain],
      lfoNodes: [loveShimmer],
      type: 'love_frequency'
    });
  }

  createBowlLayer(name, frequency, volume) {
    if (!this.isPlaying || !this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    const lfo = this.audioContext.createOscillator();
    const lfoGain = this.audioContext.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = Math.max(60, Math.min(2000, frequency));
    
    // Very slow LFO for organic beating
    lfo.type = 'sine';
    lfo.frequency.value = 0.05 + Math.random() * 0.03;
    lfoGain.gain.value = frequency * 0.008;
    
    lfo.connect(lfoGain);
    lfoGain.connect(oscillator.frequency);
    
    // Gentle attack like striking a bowl
    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(volume, this.audioContext.currentTime + 1);
    
    oscillator.connect(gainNode);
    gainNode.connect(this.effects.delay);

    oscillator.start();
    lfo.start();

    this.oscillators.push({ name, oscillator, gainNode, lfo });
  }

  updateDimensions(newDimensions) {
    this.dimensions = { ...this.dimensions, ...newDimensions };
    if (this.isPlaying) {
      this.updateBowlParameters();
    }
  }

  updateBowlParameters() {
    if (!this.isPlaying || this.oscillators.length === 0) return;

    this.oscillators.forEach(osc => {
      if (!osc.oscillator) return;

      let newFreq = osc.oscillator.frequency.value;
      
      switch (osc.name) {
        case 'fundamental':
          newFreq = this.dimensions.f;
          break;
        case 'harmonic_2nd':
          newFreq = this.dimensions.f * 2.1;
          break;
        case 'golden_bowl':
          newFreq = this.dimensions.f * this.dimensions.r;
          break;
        case 'deep_meditation':
          newFreq = this.dimensions.f / 2;
          break;
      }

      try {
        newFreq = Math.max(60, Math.min(2000, newFreq));
        osc.oscillator.frequency.exponentialRampToValueAtTime(
          newFreq, 
          this.audioContext.currentTime + 0.8
        );
      } catch (error) {
        // Oscillator might have been stopped
      }
    });
  }

  stopCosmicSounds() {
    this.isPlaying = false;
    this.oscillators.forEach(osc => {
      if (osc.gainNode) {
        osc.gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 2);
        setTimeout(() => {
          try {
            if (osc.oscillator) osc.oscillator.stop();
            if (osc.lfo) osc.lfo.stop();
          } catch (e) {}
        }, 2000);
      }
    });
    this.oscillators = [];
  }

  destroy() {
    this.stopCosmicSounds();
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }
}

// Global Sonic Audio System - Persistent Throughout Site
class GlobalSonicAudio {
  constructor() {
    this.audioContext = null;
    this.sonicEngine = null;
    this.enabled = false;
    this.dimensions = {
      r: 1.618, theta: 0, phi: 0, t: 1.0, f: 528, u: 1.0, infinity: 3.14159
    };
    this.isInitialized = false;
  }

  async init() {
    if (this.isInitialized) return;
    
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.sonicEngine = new CosmicAudioEngine();
      await this.sonicEngine.init();
      this.isInitialized = true;
      console.log('🎵 GLOBAL SONIC: Initialized successfully');
      return true;
    } catch (error) {
      console.log('🎵 GLOBAL SONIC: Audio not available');
      return false;
    }
  }

  async setEnabled(enabled) {
    this.enabled = enabled;
    
    if (enabled) {
      if (!this.isInitialized) {
        await this.init();
      }
      
      if (this.sonicEngine) {
        await this.sonicEngine.startCosmicSounds();
        this.sonicEngine.updateDimensions(this.dimensions);
        console.log('🎵 GLOBAL SONIC: Tibetan bowl harmonics activated');
      }
    } else {
      if (this.sonicEngine) {
        this.sonicEngine.stopCosmicSounds();
        console.log('🎵 GLOBAL SONIC: Sonic tones disabled');
      }
    }
  }

  updateDimensions(newDimensions) {
    this.dimensions = { ...this.dimensions, ...newDimensions };
    if (this.enabled && this.sonicEngine) {
      this.sonicEngine.updateDimensions(this.dimensions);
    }
  }

  isActive() {
    return this.enabled && this.isInitialized;
  }
}

// Create global sonic instance
window.globalSonicAudio = window.globalSonicAudio || new GlobalSonicAudio();

export default window.globalSonicAudio;